import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ParagraphBlockFontLookup } from '@/lib/text/types/paragraph-block-font-lookup'
import type { StowedElement } from '@/api/types/stowed-element'
import {
  type BuildStowedShapeOptions,
  type ShapeTextBakeContext,
  buildStowedElement,
} from '@/lib/elements/stowed-element-builder'
import type { DocumentProjection } from '@/lib/canvas/types/document-projection'
import { hasTextOnPathContent } from '@/lib/shapes/text-on-path-session'
import { readTextOnPathCharacters } from '@/lib/shapes/text-on-path-characters'

/**
 * `buildStowedElement` with the shape-text bake context attached — **the single entry point for every
 * element payload built in this file**, so no save path can transcribe a layout the canvas never
 * drew.
 *
 * `bakeShapeTextWireLines` re-lays the frame's text out to produce the wire's visual `<TL>`/`<TR>`
 * rows, and it has to do that with the same inputs the renderer used: the spread's wrap context and
 * the frame's chain state. Neither is reachable from `lib/` — computing the first needs the whole
 * spread's geometry and z-order — which is why the resolver is threaded in from here rather than
 * looked up inside the builder.
 *
 * Takes a resolver rather than a resolved value because the call sites below pass wildly different
 * shape expressions (a manager lookup, a spread literal, a freshly converted clone); handing each one
 * its own pre-computed context would mean duplicating the lookup 21 times.
 *
 * The group payload builders in `lib/groups/` still call `buildStowedElement` directly and bake
 * without any of this — they do not even pass a font lookup, so their baked rows already describe a
 * layout with the wrong glyph metrics. Narrowing that is a separate change; this closes the path
 * every ordinary element edit takes.
 */
export function buildStowedShapeElement(
  resolveBake: (shape: ShapeData) => ShapeTextBakeContext,
  shape: ShapeData,
  options?: BuildStowedShapeOptions,
  projection?: DocumentProjection,
  fonts?: ParagraphBlockFontLookup
): StowedElement {
  return buildStowedElement(
    shape,
    {
      ...options,
      shapeText: resolveBake(shape),
      // Text on a Path's `<XT>` half is readable only off the laid-out `<textPath>` in the DOM
      // (WMPCM-9542) — `lib/` cannot reach it, so the bake is resolved here, at the same seam and
      // for the same reason as `shapeText` above. Every element PUT for the host shape re-reads it,
      // not just the one the action bar's own commit raises: `elements/update` is a FULL element
      // replacement, so a plain move or resize that omitted `textOnPathElement` would delete the
      // text — and the path the characters sit on has just changed anyway.
      ...(hasTextOnPathContent(shape.textOnPath)
        ? {
            textOnPathCharacters: readTextOnPathCharacters({
              shapeId: shape.id,
              text: shape.textOnPath.text,
              width: shape.width,
              height: shape.height,
            }),
          }
        : {}),
    },
    projection,
    fonts
  )
}
